import { format, subDays, subHours } from 'date-fns';

const numberOfDemoRequests = import.meta.env.VITE_NUM_OF_REQUESTS_TO_CREATE || 256; // Default to 256 if not set

export interface Customer {
  id: number;
  caseId: string;
  caseStatus: string;
  name: string;
  email: string; // Email address of the customer
  phoneNumber: string;
  requiredDocuments: DocumentRequirement[];
  optionalDocuments: DocumentRequirement[];
  uploadedDocuments: Document[];
  lastUploadDate: string | null;
  lastReminderSent: string | null;
}

export interface DocumentRequirement {
  id: number;
  nameKey: string;  // Translation key for the document name
  descriptionKey: string;  // Translation key for the document description
}

export interface Document {
  id: number;
  requirementId: number;
  nameKey: string;  // Translation key for the document name
  fileName: string;
  fileSize: string;
  uploadDate: string;
  isRequired: boolean;
}

// Generate random date within the last 60 days
const randomDate = () => {
  const daysAgo = Math.floor(Math.random() * 24) + 1;
  const hoursAgo = Math.floor(Math.random() * 24);
  return format(subHours(subDays(new Date(), daysAgo), hoursAgo), 'yyyy-MM-dd HH:mm:ss');
};

// List of required document types
const requiredDocumentTypes: DocumentRequirement[] = [
  { id: 1, nameKey: 'docIdCard', descriptionKey: 'descIdCard' },
  { id: 2, nameKey: 'docProofOfAddress', descriptionKey: 'descProofOfAddress' },
  { id: 3, nameKey: 'docIncomeStatement', descriptionKey: 'descIncomeStatement' },
  { id: 4, nameKey: 'docTaxReturn', descriptionKey: 'descTaxReturn' },
  { id: 5, nameKey: 'docBankStatement', descriptionKey: 'descBankStatement' }
];

// List of optional document types
const optionalDocumentTypes: DocumentRequirement[] = [
  { id: 6, nameKey: 'docInsuranceInfo', descriptionKey: 'descInsuranceInfo' },
  { id: 7, nameKey: 'docReferenceLetters', descriptionKey: 'descReferenceLetters' },
  { id: 8, nameKey: 'docAdditionalSupporting', descriptionKey: 'descAdditionalSupporting' }
];

// File name extensions
const fileExtensions = ['.pdf', '.jpg', '.png', '.doc', '.docx'];

// File sizes
const fileSizes = ['1.2 MB', '3.5 MB', '824 KB', '2.1 MB', '4.3 MB', '512 KB'];

// Lists for generating random customer data
const firstNames = [
  'John', 'Mary', 'James', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth',
  'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Sarah', 'Charles', 'Karen',
  'Christopher', 'Nancy', 'Daniel', 'Lisa', 'Matthew', 'Margaret', 'Anthony', 'Betty', 'Mark', 'Sandra',
  'Donald', 'Ashley', 'Steven', 'Dorothy', 'Paul', 'Kimberly', 'Andrew', 'Emily', 'Joshua', 'Donna',
  'Kenneth', 'Michelle', 'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa', 'Edward', 'Deborah',
  'Ronald', 'Stephanie', 'Timothy', 'Rebecca', 'Jason', 'Laura', 'Jeffrey', 'Sharon', 'Ryan', 'Cynthia',
  'Jacob', 'Kathleen', 'Gary', 'Amy', 'Nicholas', 'Angela', 'Eric', 'Shirley', 'Jonathan', 'Anna',
  'Stephen', 'Ruth', 'Larry', 'Brenda', 'Justin', 'Pamela', 'Scott', 'Nicole', 'Brandon', 'Katherine',
  'Benjamin', 'Samantha', 'Samuel', 'Christine', 'Gregory', 'Catherine', 'Alexander', 'Virginia', 'Frank', 'Debra',
  'Patrick', 'Rachel', 'Raymond', 'Janet', 'Jack', 'Emma', 'Dennis', 'Carolyn', 'Jerry', 'Maria',
  'Tyler', 'Heather', 'Aaron', 'Diane', 'Jose', 'Julie', 'Adam', 'Joyce', 'Nathan', 'Victoria',
  'Henry', 'Kelly', 'Zachary', 'Christina', 'Douglas', 'Lauren', 'Peter', 'Joan', 'Kyle', 'Evelyn',
  'Noah', 'Olivia', 'Ethan', 'Judith', 'Jeremy', 'Megan', 'Walter', 'Cheryl', 'Christian', 'Andrea',
  'Keith', 'Hannah', 'Roger', 'Martha', 'Terry', 'Jacqueline', 'Gerald', 'Frances', 'Harold', 'Gloria',
  'Sean', 'Ann', 'Austin', 'Teresa', 'Carl', 'Kathryn', 'Arthur', 'Sara', 'Lawrence', 'Janice',
  'Dylan', 'Jean', 'Jesse', 'Alice', 'Jordan', 'Madison', 'Bryan', 'Doris', 'Billy', 'Abigail',
  'Bruce', 'Julia', 'Gabriel', 'Judy', 'Joe', 'Grace', 'Logan', 'Denise', 'Alan', 'Amber',
  'Juan', 'Marilyn', 'Albert', 'Beverly', 'Willie', 'Danielle', 'Elijah', 'Theresa', 'Wayne', 'Sophia',
  'Randy', 'Marie', 'Roy', 'Diana', 'Vincent', 'Brittany', 'Ralph', 'Natalie', 'Eugene', 'Isabella',
  'Russell', 'Charlotte', 'Bobby', 'Rose', 'Mason', 'Alexis', 'Philip', 'Kayla', 'Louis', 'Lori'
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Jones', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore', 'Taylor',
  'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Garcia', 'Martinez', 'Robinson',
  'Clark', 'Rodriguez', 'Lewis', 'Lee', 'Walker', 'Hall', 'Allen', 'Young', 'Hernandez', 'King',
  'Wright', 'Lopez', 'Hill', 'Scott', 'Green', 'Adams', 'Baker', 'Gonzalez', 'Nelson', 'Carter',
  'Mitchell', 'Perez', 'Roberts', 'Turner', 'Phillips', 'Campbell', 'Parker', 'Evans', 'Edwards', 'Collins',
  'Stewart', 'Sanchez', 'Morris', 'Rogers', 'Reed', 'Cook', 'Morgan', 'Bell', 'Murphy', 'Bailey',
  'Rivera', 'Cooper', 'Richardson', 'Cox', 'Howard', 'Ward', 'Torres', 'Peterson', 'Gray', 'Ramirez',
  'James', 'Watson', 'Brooks', 'Kelly', 'Sanders', 'Price', 'Bennett', 'Wood', 'Barnes', 'Ross',
  'Henderson', 'Coleman', 'Jenkins', 'Perry', 'Powell', 'Long', 'Patterson', 'Hughes', 'Flores', 'Washington',
  'Butler', 'Simmons', 'Foster', 'Gonzales', 'Bryant', 'Alexander', 'Russell', 'Griffin', 'Diaz', 'Hayes',
  'Myers', 'Ford', 'Hamilton', 'Graham', 'Sullivan', 'Wallace', 'Woods', 'Cole', 'West', 'Jordan',
  'Owens', 'Reynolds', 'Fisher', 'Ellis', 'Harrison', 'Gibson', 'McDonald', 'Cruz', 'Marshall', 'Ortiz',
  'Gomez', 'Murray', 'Freeman', 'Wells', 'Webb', 'Simpson', 'Stevens', 'Tucker', 'Porter', 'Hunter',
  'Hicks', 'Crawford', 'Henry', 'Boyd', 'Mason', 'Morales', 'Kennedy', 'Warren', 'Dixon', 'Ramos',
  'Reyes', 'Burns', 'Gordon', 'Shaw', 'Holmes', 'Rice', 'Robertson', 'Hunt', 'Black', 'Daniels',
  'Palmer', 'Mills', 'Nichols', 'Grant', 'Knight', 'Ferguson', 'Rose', 'Stone', 'Hawkins', 'Dunn',
  'Perkins', 'Hudson', 'Spencer', 'Gardner', 'Stephens', 'Payne', 'Pierce', 'Berry', 'Matthews', 'Arnold',
  'Wagner', 'Willis', 'Ray', 'Watkins', 'Olson', 'Carroll', 'Duncan', 'Snyder', 'Hart', 'Cunningham',
  'Bradley', 'Lane', 'Andrews', 'Ruiz', 'Harper', 'Fox', 'Riley', 'Armstrong', 'Carpenter', 'Weaver',
  'Greene', 'Lawrence', 'Elliott', 'Chavez', 'Sims', 'Austin', 'Peters', 'Kelley', 'Franklin', 'Lawson',
  'Hansen', 'Hoffman', 'McCarthy', 'Fleming', 'McKenzie', 'Baldwin', 'Harmon', 'McBride', 'Schneider', 'Walters',
  'Fletcher', 'Nash', 'Hogan', 'Holloway', 'Bass', 'Neal', 'Pope', 'Snow', 'Frost', 'Hale',
  'McClain', 'Brock', 'Summers', 'Underwood', 'Valdez', 'McGee', 'Burgess', 'Hines', 'Boyle', 'Parks',
  'Conner', 'Horton', 'Casey', 'Patton', 'Kirk', 'Hood', 'Bishop', 'McCoy', 'Mann', 'Cannon',
  'Clay', 'Klein', 'Goodman', 'Sharp', 'Bowen', 'Daniel', 'Barber', 'Cummings', 'Hines', 'Barton',
  'Bates', 'Hubbard', 'Holt', 'Tate', 'Mack', 'Hines', 'Herman', 'McGuire', 'Hodge', 'Gates'
];

// Generate a realistic file name
const generateFileName = (docNameKey: string): string => {
  // Convert translation key to a reasonable filename prefix
  // e.g., docIdCard -> id_card
  const prefix = docNameKey
    .replace(/^doc/, '')
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .substring(1);
    
  const randomNum = Math.floor(Math.random() * 10000);
  const ext = fileExtensions[Math.floor(Math.random() * fileExtensions.length)];
  return `${prefix}_${randomNum}${ext}`;
};

// Generate random phone number
const generatePhoneNumber = (): string => {
  const area = Math.floor(Math.random() * 900) + 100;
  const prefix = Math.floor(Math.random() * 900) + 100;
  const line = Math.floor(Math.random() * 9000) + 1000;
  return `(${area}) ${prefix}-${line}`;
};

// Generate random uploaded documents for a customer
const generateUploadedDocuments = (customer: Customer, count: number): Document[] => {
  const docs: Document[] = [];
  const allRequirements = [...customer.requiredDocuments, ...customer.optionalDocuments];
  
  // Randomly select requirements to have uploads
  const selectedRequirements = [...allRequirements]
    .sort(() => 0.5 - Math.random())
    .slice(0, Math.min(count, allRequirements.length));
  
  for (let i = 0; i < selectedRequirements.length; i++) {
    const requirement = selectedRequirements[i];
    docs.push({
      id: i + 1,
      requirementId: requirement.id,
      nameKey: requirement.nameKey,
      fileName: generateFileName(requirement.nameKey),
      fileSize: fileSizes[Math.floor(Math.random() * fileSizes.length)],
      uploadDate: randomDate(),
      isRequired: requirement.id <= 5 // IDs 1-5 are required
    });
  }
  return docs.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
};

// Create a function to generate a complete set of required documents
const generateAllRequiredDocuments = (customer: Customer): Document[] => {
  const docs: Document[] = [];
  
  // Generate documents for all required document types
  for (let i = 0; i < customer.requiredDocuments.length; i++) {
    const requirement = customer.requiredDocuments[i];
    docs.push({
      id: i + 1,
      requirementId: requirement.id,
      nameKey: requirement.nameKey,
      fileName: generateFileName(requirement.nameKey),
      fileSize: fileSizes[Math.floor(Math.random() * fileSizes.length)],
      uploadDate: randomDate(),
      isRequired: true
    });
  }
  
  // Add some optional documents as well
  const optionalCount = Math.floor(Math.random() * customer.optionalDocuments.length);
  const selectedOptional = [...customer.optionalDocuments]
    .sort(() => 0.5 - Math.random())
    .slice(0, optionalCount);
    
  let idCounter = customer.requiredDocuments.length + 1;
  
  for (const requirement of selectedOptional) {
    docs.push({
      id: idCounter++,
      requirementId: requirement.id,
      nameKey: requirement.nameKey,
      fileName: generateFileName(requirement.nameKey),
      fileSize: fileSizes[Math.floor(Math.random() * fileSizes.length)],
      uploadDate: randomDate(),
      isRequired: false
    });
  }
  
  return docs.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
};

// Generate random customer with proper document requirements
const generateRandomCustomer = (id: number): Customer => {
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const currentYear = new Date().getFullYear();
  const yearSuffix = currentYear.toString().slice(2);
  const caseId = `CASE-${yearSuffix}-${(id + 1).toString().padStart(3, '0')}`;
  const caseStatus = `In Progress`;

  const customer: Customer = {
    id,
    caseId,
    caseStatus,
    name: `${firstName} ${lastName}`,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    phoneNumber: generatePhoneNumber(),
    requiredDocuments: [...requiredDocumentTypes],
    optionalDocuments: [...optionalDocumentTypes],
    uploadedDocuments: [],
    lastUploadDate: null,
    lastReminderSent: Math.random() > 0.1 ? randomDate() : null,
  };

  // Randomly decide how many documents are uploaded (0 to all required + some optional)
  const documentCompletionRatio = Math.random();
  let uploadedDocCount;

  if (documentCompletionRatio < 0.15) {
    // No documents uploaded
    uploadedDocCount = 0;
  } else if (documentCompletionRatio < 0.40) {
    // Some documents (1-2)
    uploadedDocCount = Math.floor(Math.random() * 2) + 1;
  } else if (documentCompletionRatio < 0.70) {
    // About half documents (2-3)
    uploadedDocCount = Math.floor(Math.random() * 2) + 2;
  } else if (documentCompletionRatio < 0.85) {
    // Most documents (3-4)
    uploadedDocCount = Math.floor(Math.random() * 2) + 3;
  } else {
    // All required + some optional documents
    customer.uploadedDocuments = generateAllRequiredDocuments(customer);
    return {
      ...customer,
      lastUploadDate: customer.uploadedDocuments.length > 0
        ? customer.uploadedDocuments[0].uploadDate
        : null
    };
  }

  customer.uploadedDocuments = generateUploadedDocuments(customer, uploadedDocCount);
  
  // Update lastUploadDate based on uploaded documents
  if (customer.uploadedDocuments.length > 0) {
    customer.lastUploadDate = customer.uploadedDocuments[0].uploadDate;
  }

  return customer;
};

// Generate initial set of customers
const initialCustomers: Customer[] = [
  {
    id: 0,
    caseId: 'CASE-2023-001',
    caseStatus: 'In Progress',
    name: 'John Smith',
    email: 'john.smith@example.com',
    phoneNumber: '(555) 123-4567',
    requiredDocuments: [...requiredDocumentTypes],
    optionalDocuments: [...optionalDocumentTypes],
    uploadedDocuments: [],
    lastUploadDate: null,
    lastReminderSent: format(subDays(new Date(), 2), 'yyyy-MM-dd HH:mm:ss')
  },
  {
    id: 1,
    caseId: 'CASE-2023-002',
    caseStatus: 'In Progress',
    name: 'Maria Rodriguez',
    email: 'maria.rodriguez@example.com',
    phoneNumber: '(555) 987-6543',
    requiredDocuments: [...requiredDocumentTypes],
    optionalDocuments: [...optionalDocumentTypes],
    uploadedDocuments: [],
    lastUploadDate: null,
    lastReminderSent: null
  },
  {
    id: 2,
    caseId: 'CASE-2023-003',
    caseStatus: 'In Progress',
    name: 'James Johnson',
    email: 'james.johnson@example.com',
    phoneNumber: '(555) 222-3333',
    requiredDocuments: [...requiredDocumentTypes],
    optionalDocuments: [...optionalDocumentTypes],
    uploadedDocuments: [],
    lastUploadDate: null,
    lastReminderSent: format(subDays(new Date(), 10), 'yyyy-MM-dd HH:mm:ss')
  }
];

// Make Maria Rodriguez have all required documents completed
initialCustomers[1].uploadedDocuments = generateAllRequiredDocuments(initialCustomers[1]);

// Generate uploaded documents for other customers
initialCustomers[0].uploadedDocuments = generateUploadedDocuments(initialCustomers[0], 2);
initialCustomers[2].uploadedDocuments = generateUploadedDocuments(initialCustomers[2], 3);

// Update lastUploadDate based on uploaded documents
initialCustomers.forEach(customer => {
  if (customer.uploadedDocuments.length > 0) {
    customer.lastUploadDate = customer.uploadedDocuments[0].uploadDate;
  }
});

// Generate the larger dataset (hundreds of rows) only once on app startup
let customers: Customer[] = [];

const initializeCustomers = () => {
  // console.log("customers.length: " + customers.length);
  if (customers.length === 0) {
    const generateLargeDataset = (count: number): Customer[] => {
      const dataset = [...initialCustomers]; // Start with our initial customers
      
      // Add randomly generated customers
      for (let i = initialCustomers.length; i < count; i++) {
        dataset.push(generateRandomCustomer(i));
      }
      
      return dataset;
    };

    // Generate 300 customer records
    customers = generateLargeDataset(numberOfDemoRequests);
  }
};

// initializeCustomers();

export { customers, initializeCustomers };

export const getCustomerByCase = (caseId: string): Customer | undefined => {
  return customers.find(customer => customer.caseId === caseId);
};
